import packageExclusion from '@/configs/package-exclusion.json';

interface PackageExclusion {
	/** Clear text name of the package to make it easier to know which package this applies to. */
	clearName: string;

	/**
	 * List of countries to exclude the package in for logged out users.
	 * If `true` the package will be excluded in ALL countries.
	 */
	loggedOut?: string[] | true;

	/**
	 * List of countries to exclude the package in for logged in users.
	 * If `true` the package will be excluded in ALL countries.
	 */
	loggedIn?: string[] | true;

	/** List of entity IDs on which to exclude the package. */
	entityId?: string[];

	/**
	 * Exception conditions on which to include the package despite the lists
	 * defined in `loggedOut` and `loggedIn`.
	 *
	 * Here you can also overwrite exclusion for Pro and non Pro user states.
	 */
	include?: {
		/** Optional list of countries to include the package in for logged out users. */
		loggedOut?: string[];

		/** Optional list of countries to include the package in for logged in users. */
		loggedIn?: string[];

		/** Optional list of countries to include the package in for pro users. */
		proUser?: string[] | true;

		/** Optional list of countries to include the package in for non pro users. */
		nonProUser?: string[] | true;
	};
}

/** Package exclusion rule configuration. It is keyed by the package short name. */
type PackageExclusions = Record<string, PackageExclusion>;

/** Given user and application states, returns a list of package shortnames that should be hidden. */
export function getExcludedPackageShortNames(
	isLoggedIn: boolean,
	entityId: string | undefined,
	country: string,
	isProUser: boolean
) {
	return Object.entries(packageExclusion as PackageExclusions)
		.filter(([_, rules]) => shouldExcludePackage({ rules, isLoggedIn, country, isProUser, entityId }))
		.map(([shortName]) => shortName);
}

interface ExclusionCheckArguments {
	rules: PackageExclusion;
	isLoggedIn: boolean;
	country: string;
	isProUser: boolean;
	entityId: string | undefined;
}

function shouldExcludePackage({ rules, isLoggedIn, country, entityId, isProUser }: ExclusionCheckArguments) {
	const countryList = isLoggedIn ? rules.loggedIn : rules.loggedOut;
	if (countryList == null) return false;

	const isExcluded = countryList === true || countryList.includes(country);
	if (!isExcluded) return false;

	const countryExceptionList = isLoggedIn ? rules.include?.loggedIn : rules.include?.loggedOut;
	if (countryExceptionList?.includes(country)) return false;

	if (isLoggedIn) {
		const proUserRules = isProUser ? rules.include?.proUser : rules.include?.nonProUser;
		if (proUserRules === true || proUserRules?.includes(country)) return false;
	}

	if (entityId != null && rules.entityId?.includes(entityId)) return false;

	return true;
}
